import { getPlan, type Plan } from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { kmApart } from '../../lib/map';
import { keptOf } from '../../lib/offlineMaps';

/**
 * Today with no network (the `OfflineMaps` mockup): the stop's map is on the phone, kept on the
 * day it says, and the day's next place, how far on foot from where the phone says one is — GPS
 * works with no network, the plan and the map are the phone's own.
 */
export function OfflineToday({
  trip,
  day,
  stop: at,
}: {
  trip: string;
  day: string;
  stop: number;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [plan, setPlan] = useState<Plan | null>(null);
  const [walk, setWalk] = useState<{ km: number; minutes: number } | null>(null);
  const [lost, setLost] = useState(false);

  useEffect(() => {
    let live = true;
    getPlan(trip).then(
      (read) => {
        if (live) setPlan(read);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [trip]);

  const stop = plan?.stops[at];
  const kept = stop ? keptOf(trip)[stop.id] : undefined;
  if (!stop || !kept?.at) return null;
  const next = stop.agenda
    .flatMap((thing) =>
      thing.day === day && thing.place
        ? [{ name: thing.name, starts: thing.starts ?? '99', place: thing.place }]
        : [],
    )
    .sort((a, b) => a.starts.localeCompare(b.starts))[0];
  const keptOn = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(kept.at));

  const locate = (place: { lat: number; lon: number }) => {
    setLost(false);
    navigator.geolocation.getCurrentPosition(
      (here) => {
        // On foot a quarter longer than the straight line, at five kilometres an hour.
        const km =
          kmApart({ lat: here.coords.latitude, lon: here.coords.longitude }, place) * 1.25;
        setWalk({
          km: Math.round(km * 10) / 10,
          minutes: Math.max(1, Math.round((km / 5) * 60)),
        });
      },
      () => {
        setLost(true);
      },
    );
  };

  return (
    <section className="panel today-offline" aria-labelledby="today-offline">
      <h2 id="today-offline">{t('offline.today', { day: keptOn })}</h2>
      {next && (
        <>
          <p>
            <b>{t('offline.next', { name: next.name })}</b>
            {walk && (
              <>
                <br />
                {t('offline.walk', {
                  km: walk.km.toLocaleString(lang),
                  minutes: walk.minutes,
                })}
              </>
            )}
          </p>
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              locate(next.place);
            }}
          >
            {t('offline.whereAmI')}
          </button>
          {lost && (
            <p className="form-failed" role="alert">
              {t('offline.noGps')}
            </p>
          )}
        </>
      )}
      <p className="quiet today-offline-note">{t('offline.gpsHow')}</p>
    </section>
  );
}
